import { ConsentStatuses } from './constants'
import { Logger } from 'lib'
// eslint-disable-next-line no-unused-vars
import { IConsentManagementService } from './types'

/**
 * The various kinds of Osano events.
 */
// eslint-disable-next-line no-unused-vars
const EventTypes = Object.freeze({
  /**
   * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#-osano-cm-consent-saved}
   */
  ConsentSaved: 'osano-cm-consent-saved',

  /**
   * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#events-cookie-blocked}
   */
  CookieBlocked: 'osano-cm-cookie-blocked',

  /**
   * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#events-iframe-blocked}
   */
  IframeBlocked: 'osano-cm-iframe-blocked',

  /**
   * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#-osano-cm-script-blocked}
   */
  ScriptBlocked: 'osano-cm-script-blocked'
})

/**
 * @typedef {typeof EventTypes[keyof typeof EventTypes]} EventType
 */

/**
 * The various kinds of Osano events supported by the preloaded version of Osano.
 * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#adding-event-listeners}
 */
const PreloadedEventTypes = Object.freeze({
  /**
   * **NOTE:** The link references `osano-cm-consent-saved`. However, since this is the preloaded
   * event, the actual value is different. This link is used because the values passed to the
   * callback are the same.
   * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#-osano-cm-consent-saved}
   */
  ConsentSaved: 'onConsentSaved',

  /**
   * **NOTE:** The link references `osano-cm-cookie-blocked`. However, since this is the preloaded
   * event, the actual value is different. This link is used because the values passed to the
   * callback are the same.
   * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#events-cookie-blocked}
   */
  CookieBlocked: 'onCookieBlocked',

  /**
   * **NOTE:** The link references `osano-cm-iframe-blocked`. However, since this is the preloaded
   * event, the actual value is different. This link is used because the values passed to the
   * callback are the same.
   * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#events-iframe-blocked}
   */
  IframeBlocked: 'onIframeBlocked',

  /**
   * **NOTE:** The link references `osano-cm-script-blocked`. However, since this is the preloaded
   * event, the actual value is different. This link is used because the values passed to the
   * callback are the same.
   * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#-osano-cm-script-blocked}
   */
  ScriptBlocked: 'onScriptBlocked'
})

/**
 * @typedef {typeof PreloadedEventTypes[keyof typeof PreloadedEventTypes]} PreloadedEventType
 */

/**
 * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#the-consent-object}
 * @typedef {Object} Consent
 * @property {'ACCEPT' | 'DENY'} ANALYTICS Helps the website operators understand how its website performs, how visitors interact with the site, and whether there may be technical issues.
 * @property {'ACCEPT'} ESSENTIAL Required to enable basic website functionality. Users may not disable essential cookies.
 * @property {'ACCEPT' | 'DENY'} MARKETING Targeted advertising, i.e., advertisements that are more relevant to the user and their interests.
 * @property {'ACCEPT' | 'DENY'} PERSONALIZATION Allow the website to remember choices you make and provide enhanced, more personal features.
 */

/**
 * @template {EventType} TEventType
 * @callback EventListenerCallback
 * @param {TEventType extends 'osano-cm-consent-saved' ? Consent : never} data
 * @returns {void}
 */

/**
 * @template {PreloadedEventType} TPreloadedEventType
 * @callback PreloadedEventListenerCallback
 * @param {TPreloadedEventType extends 'onConsentSaved' ? Consent : string} data
 * @returns {void}
 */

/**
 * The pre-loaded instance of Osano.
 * @typedef {<TPreloadedEventType extends PreloadedEventType>(eventType: TPreloadedEventType, callback: PreloadedEventListenerCallback<TPreloadedEventType>) => void} PreloadedOsano
 */

/**
 * The Osano Consent Management Platform, or CMP, JavaScript API.
 * @see {@link https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api}
 * @typedef {PreloadedOsano & { cm: { addEventListener: <TEventType extends EventType>(eventType: TEventType, callback: EventListenerCallback<TEventType>) => void, getConsent: function(): Consent, locale: string, showDrawer: function(): void, userData: number | string } }} Osano
 */

/**
 * @typedef {Window & typeof globalThis & { Osano: PreloadedOsano & Partial<Osano> }} OsanoWindow
 */

/**
 * @param {unknown} value
 * @returns {value is Osano}
 */
function isOsanoReady (value) {
  if (!value || typeof value !== 'function') {
    return false
  }

  if (!('cm' in value) || typeof value.cm !== 'object' || !value.cm) {
    return false
  }

  if (
    !('addEventListener' in value.cm)
    || typeof value.cm.addEventListener !== 'function'
  ) {
    return false
  }

  if (
    !('getConsent' in value.cm)
    || typeof value.cm.getConsent !== 'function'
  ) {
    return false
  }

  if (
    !('showDrawer' in value.cm)
    || typeof value.cm.showDrawer !== 'function'
  ) {
    return false
  }

  if (
    !('userData' in value.cm)
    || typeof value.cm.userData !== 'number'
      && typeof value.cm.userData !== 'string'
  ) {
    return false
  }

  return true
}

/**
 * @param {unknown} value
 * @returns {value is OsanoWindow}
 */
function isOsanoWindow (value) {
  if (typeof value !== 'object' || !value) {
    return false
  }

  if (!('Osano' in value) || typeof value.Osano !== 'function') {
    return false
  }

  return true
}

/**
 * @implements {IConsentManagementService}
 */
export class OsanoConsentManagementService {
  #logger

  constructor () {
    this.#logger = new Logger(this.constructor.name)

    if (
      !process.env.CONSENT__OSANO_CONFIG_ID
      || !process.env.CONSENT__OSANO_CUSTOMER_ID
    ) {
      return
    }

    if (!isOsanoWindow(window)) {
      this.#logger.debug(
        'Osano is not available. It must at least be preloaded.'
      )
      return
    }

    window.Osano(PreloadedEventTypes.CookieBlocked, (cookie) => {
      this.#logger.warn('Cookie blocked:', cookie)
    })

    window.Osano(PreloadedEventTypes.IframeBlocked, (src) => {
      this.#logger.warn('Iframe blocked:', src)
    })

    window.Osano(PreloadedEventTypes.ScriptBlocked, (src) => {
      this.#logger.warn('Script blocked:', src)
    })
  }

  /**
   * @returns {import('./types').ConsentState}
   */
  getConsent () {
    if (
      !process.env.CONSENT__OSANO_CONFIG_ID
      || !process.env.CONSENT__OSANO_CUSTOMER_ID
    ) {
      return { analytics: ConsentStatuses.Pending }
    }

    if (!isOsanoWindow(window)) {
      this.#logger.debug(
        'Osano is not available. It must at least be preloaded.'
      )
      return { analytics: ConsentStatuses.Pending }
    }

    if (!isOsanoReady(window.Osano)) {
      return { analytics: ConsentStatuses.Pending }
    }

    const consent = window.Osano.cm.getConsent()

    return {
      analytics: consent.ANALYTICS
    }
  }

  renderPreferences () {
    if (
      !process.env.CONSENT__OSANO_CONFIG_ID
      || !process.env.CONSENT__OSANO_CUSTOMER_ID
    ) {
      this.#logger.debug('Osano is not enabled.')
      return
    }

    if (!isOsanoReady(window.Osano)) {
      return
    }

    window.Osano.cm.showDrawer()
  }
}
